import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip, Colors, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import styled, { css } from 'styled-components';
import { IoMdTrash } from 'react-icons/io';

// constants
import Rules from 'restrictions/roles/constants/rules';

// actions
import { openModal } from 'ducks/merchantDucks/modal/actions';
import { deleteItem, ItemType } from 'ducks/merchantDucks/items/actions';
import { removeVoucher } from 'ducks/merchantDucks/vouchers/actions';

// types
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// styled components
import { StyledReactIcon } from 'pages/MerchantPages/Audience/components/AudienceDetailsBox';

// components
import ConfirmModal from 'components/Modals/ConfirmModal';
import RestrictedByRule from 'restrictions/roles/components/RestrictedByRule';

const StyledIcon = styled(StyledReactIcon)<{ disabled?: boolean }>`
  vertical-align: middle;
  margin-left: 0.5rem;

  :hover {
    transform: scale(1.2);
    transition: 0.2s ease;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;

      :hover {
        & > svg {
          cursor: default;
        }
      }
    `}
`;

interface ConfirmModalPropsTypes {
  handleConfirm: () => void;
  modalName: ModalNames;
  message: string;
  title: string;
  buttonText: string;
  subheader?: string;
}

interface Props {
  itemType: string;
  itemId: number;
}

const ItemDelete: FC<Props> = ({ itemType, itemId }) => {
  const dispatch = useDispatch();

  const showConfirmModal = () => {
    dispatch(openModal(ModalNames.AssetRemove));
  };

  const handleItemRemove = () => {
    if (itemType === ItemType.Voucher) {
      dispatch(removeVoucher(itemId));
      return;
    }
    dispatch(deleteItem(itemId, itemType));
  };

  const isAsset = itemType.includes('asset');
  const isVoucher = itemType === ItemType.Voucher;
  const buttonDeleteText = isAsset ? 'asset' : 'package';
  const buttonType = isAsset ? ItemType.Asset : itemType;
  const confirmModalProps: ConfirmModalPropsTypes = {
    modalName: ModalNames.AssetRemove,
    handleConfirm: handleItemRemove,
    message: `Are you sure you want to deactivate the ${buttonType}?`,
    title: `Deactivate ${isAsset ? 'asset' : itemType}`,
    buttonText: 'deactivate',
  };

  if (itemType !== ItemType.Voucher) {
    confirmModalProps.subheader = `You can always reactivate the ${buttonType} later.`;
  } else {
    confirmModalProps.message = 'Are you sure you want to delete the voucher?';
    confirmModalProps.title = 'Delete voucher';
    confirmModalProps.buttonText = 'delete';
  }

  return (
    <AnalyticsComponent>
      {({ pages, tracker, merchantId, ip }) => (
        <>
          <RestrictedByRule rule={Rules.READONLY_ASSETS_AND_PACKAGES}>
            <Tooltip placement="left" content={confirmModalProps.title}>
              <StyledIcon
                as={IoMdTrash}
                color={Colors.red}
                onClick={() => {
                  tracker.track({
                    event: 'click',
                    type: 'button',
                    tag: `icon_${isAsset || isVoucher ? 'delete' : 'deactivate'}_${
                      isVoucher ? 'voucher' : buttonDeleteText
                    }`,
                    pages,
                    merchantId,
                    ip,
                  });
                  return showConfirmModal();
                }}
              />
            </Tooltip>
          </RestrictedByRule>
          <ConfirmModal {...confirmModalProps} />
        </>
      )}
    </AnalyticsComponent>
  );
};

export default ItemDelete;
